/* eslint-disable react-hooks/exhaustive-deps */
/* eslint-disable @typescript-eslint/no-unused-vars */
import React, { useState, useCallback } from "react";
import {
  View,
  Text,
  StyleSheet,
  ScrollView,
  TouchableOpacity,
  Platform,
  useWindowDimensions,
} from "react-native";
import Svg, { Path } from "react-native-svg";
import { useSelector } from 'react-redux';
import { selectSiteEvalActionPlans } from '../../../store/siteEvalRedirectSlice';
import LhToggleSwitch from "../../../components/LhToggleSwitch";
import FontFamily from "../../../constants/fonts";
import DLOTooltip from "../../../modules/dlo/components/DLOTooltip";
import { formatUserName } from "../../../utils/emailTouserName";
import { useTheme } from "../../../theme";

// SVG Icons for Figma design
const EditIcon: React.FC = () => (
  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <Path
      d="M10.545 6.765L11.235 7.455L4.44 14.25H3.75V13.56L10.545 6.765ZM13.245 2.25C13.0575 2.25 12.8625 2.325 12.72 2.4675L11.3475 3.84L14.16 6.6525L15.5325 5.28C15.825 4.9875 15.825 4.515 15.5325 4.2225L13.7775 2.4675C13.6275 2.3175 13.44 2.25 13.245 2.25ZM10.545 4.6425L2.25 12.9375V15.75H5.0625L13.3575 7.455L10.545 4.6425Z"
      fill="#005CBC"
    />
  </Svg>
);

const InfoIcon: React.FC = () => (
  <Svg width="18" height="18" viewBox="0 0 18 18" fill="none">
    <Path
      d="M8.25 5.25H9.75V6.75H8.25V5.25ZM8.25 8.25H9.75V12.75H8.25V8.25ZM9 1.5C4.86 1.5 1.5 4.86 1.5 9C1.5 13.14 4.86 16.5 9 16.5C13.14 16.5 16.5 13.14 16.5 9C16.5 4.86 13.14 1.5 9 1.5ZM9 15C5.6925 15 3 12.3075 3 9C3 5.6925 5.6925 3 9 3C12.3075 3 15 5.6925 15 9C15 12.3075 12.3075 15 9 15Z"
      fill="#005CBC"
    />
  </Svg>
);

// Add Icon for per-row Add Action Plan button
const AddIcon: React.FC = () => (
  <Svg width="12" height="12" viewBox="0 0 12 12" fill="none">
    <Path d="M6 0.5V11.5M0.5 6H11.5" stroke="#005CBC" strokeLinecap="round" strokeLinejoin="round" />
  </Svg>
);

// Helper function to check if action plan exists (using Redux)
const checkIfActionPlanExistsInRedux = (actionPlans: any[], itemId: string, evalId?: number | null) => {
  try {
    const uniqueKey = evalId ? `${evalId}_${itemId}` : itemId;
    return actionPlans.some((plan: any) => plan.itemId === uniqueKey || plan.relatedId === uniqueKey);
  } catch (error) {
    console.warn('Error checking action plan existence:', error);
    return false;
  }
};

// Helper function to get stored action plan data (using Redux)
const getStoredActionPlanFromRedux = (actionPlans: any[], itemId: string, evalId?: number | null) => {
  try {
    const uniqueKey = evalId ? `${evalId}_${itemId}` : itemId;
    return actionPlans.find((plan: any) => plan.itemId === uniqueKey || plan.relatedId === uniqueKey) || null;
  } catch (error) {
    console.warn('Error retrieving action plan data:', error);
    return null;
  }
};

// Helper to get custom labels for Lighthouse Execution
const getCustomLabels = (subHeaderTitle?: string) => {
  if (subHeaderTitle && subHeaderTitle.trim().toLowerCase() === "lighthouse execution") {
    return {
      Yes: "Sustained",
      No: "Need Improvement",
      "N/A": "Not present",
    };
  }
  return {
    Yes: "Yes",
    No: "No",
    "N/A": "N/A",
  };
};

// Helper to get first subheader title
const getFirstSubHeaderTitle = (subHeadersObj: any) => {
  const subHeadersArr = Object.values(subHeadersObj);
  if (subHeadersArr.length > 0) {
    const first = subHeadersArr[0];
    if (first && typeof first === 'object' && 'subHeader' in first) {
      const subHeaderObj = (first as any).subHeader;
      if (typeof subHeaderObj === 'string') {
        return subHeaderObj;
      } else if (subHeaderObj && typeof subHeaderObj === 'object' && typeof subHeaderObj.text === 'string') {
        return subHeaderObj.text;
      }
    }
  }
  return undefined;
};

export type SurveyAnswer = "Yes" | "No" | "N/A" | null;

interface SurveyItem {
  id: string;
  text: string;
  type: "header" | "subheader" | "question";
  isParent?: boolean;
  parentId?: string;
  visible?: boolean;
  hasToggle?: boolean;
}

interface SurveyAnswers {
  [key: string]: any;
}

interface SiteEvalSurveyContainerProps {
  data: any[];
  walkAnswers?: { [id: string]: any };
  closeOutAnswers?: { [id: string]: { partner?: string } };
  onRadioChange?: (id: string, answer: "yes" | "no" | "na") => void;
  onAnswersChange?: (answers: SurveyAnswers, isComplete: boolean) => void;
  onToggleChange?: (id: string, value: boolean, title: string) => void;
  testID?: string;
  isViewOnlyMode?: boolean; // New prop to indicate view-only mode
  selectedEvalId?: number | null; // Evaluation ID for composite action plan keys
}

interface RadioButtonProps {
  selected: boolean;
  onPress: () => void;
  label: string;
  disabled?: boolean;
  testID?: string;
  isMobileLayout?: boolean;
}

// Radio button component exactly like TouchPointSurvey.tsx
const RadioButton: React.FC<RadioButtonProps> = ({
  selected,
  onPress,
  label,
  disabled = false,
  testID,
  isMobileLayout = false,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  return (
    <TouchableOpacity
      onPress={() => !disabled && onPress && onPress()}
      style={[
        styles.radioButtonContainer,
        // apply desktop/mobile specific container so width is fixed on desktop/iPad
        isMobileLayout ? styles.radioButtonContainerMobile : styles.radioButtonContainerDesktop,
        disabled && styles.radioButtonContainerDisabled,
      ]}
      accessibilityRole="radio"
      accessibilityState={{ checked: !!selected }}
      testID={testID}
    >
      <View style={styles.radioButtonInner}>
        <View
          style={[
            styles.radioButton,
            isDark && { borderColor: colors.brand.primary },
            disabled && styles.radioButtonDisabled,
          ]}
        >
          {selected ? (
            <View
              style={[
                styles.radioButtonSelected,
                isDark && { backgroundColor: colors.brand.primary },
                disabled && styles.radioButtonSelectedDisabled,
              ]}
            />
          ) : null}
        </View>
        {label ? (
          <Text
            style={[
              styles.radioButtonLabel,
              isDark && { color: colors.text.secondary },
              disabled && styles.radioButtonLabelDisabled,
            ]}
          >
            {label}
          </Text>
        ) : null}
      </View>
    </TouchableOpacity>
  );
};

// Question row component exactly like TouchPointSurvey.tsx
const QuestionRow: React.FC<{
  item: SurveyItem;
  answer: SurveyAnswer;
  onSelectAnswer: (id: string, answer: SurveyAnswer) => void;
  isSubData?: boolean;
  testID?: string;
  hasActionPlan?: boolean;
  storedPlan?: any;
  subHeaderTitle?: string;
  onOpenActionPlan?: (id: string, item: SurveyItem) => void;
  isMobileLayout?: boolean;
  isViewOnlyMode?: boolean;
}> = ({
  item,
  answer,
  onSelectAnswer,
  isSubData = false,
  testID,
  hasActionPlan = false,
  storedPlan,
  subHeaderTitle,
  onOpenActionPlan,
  isMobileLayout = false,
  isViewOnlyMode = false,
}) => {
  // Remove all labels from radio buttons on desktop; on mobile show labels.
  // For Lighthouse Execution on mobile show custom labels (Sustained / Need Improvement / Not present).
  const customLabels = getCustomLabels(subHeaderTitle);
  const isLighthouseExecution = !!(subHeaderTitle && String(subHeaderTitle).trim().toLowerCase() === 'lighthouse execution');
  // For mobile, force No and N/A to break into two lines so all three fit horizontally.
  const formatMobileLabel = (label: string) => {
    if (!label) return label;
    // If label already contains a space and we're in LH Execution, replace first space with a line break
    if (isLighthouseExecution && isMobileLayout) {
      const parts = label.split(' ');
      if (parts.length >= 2) {
        return parts.slice(0, 1).join(' ') + "\n" + parts.slice(1).join(' ');
      }
    }
    return label;
  };
  const answerOptions = [
    { value: "Yes" as SurveyAnswer, label: customLabels.Yes ?? 'Yes' },
    { value: "No" as SurveyAnswer, label: customLabels.No ?? 'No', labelMobile: formatMobileLabel(customLabels.No ?? 'No') },
    { value: "N/A" as SurveyAnswer, label: customLabels['N/A'] ?? 'N/A', labelMobile: formatMobileLabel(customLabels['N/A'] ?? 'N/A') },
  ];

  // Check if action plan exists for this specific question
  const hasExistingActionPlan = hasActionPlan;
  const shouldShowActionPlan = answer === "No" || hasExistingActionPlan;

  const { colors: qColors, resolvedTheme: qResolvedTheme } = useTheme();
  const qIsDark = qResolvedTheme === "dark";
  return (
    <View
      style={[
        styles.questionRow,
        isSubData && styles.subDataRow,
        isMobileLayout && styles.questionRowMobile,
        qIsDark && { borderBottomColor: qColors.border.divider },
      ]}
      testID={`${testID}-row-${item.id}`}
    >
      <View style={[styles.questionTextContainer, isMobileLayout ? styles.questionTextContainerMobile : styles.questionTextContainerDesktop]}>
        <Text style={[styles.questionText, isSubData && styles.subDataText, qIsDark && { color: qColors.text.primary }]}>
          {item.text}
        </Text>
      </View>
      <View style={[
        styles.radioButtonsContainer,
        isMobileLayout ? styles.radioButtonsContainerMobile : styles.radioButtonsContainerDesktop,
      ]}>
        {answerOptions.map((option, index) => (
          <RadioButton
            key={index}
            selected={answer === option.value}
            onPress={() => onSelectAnswer(item.id, option.value)}
            // Use mobile-specific wrapped label when available
            label={isMobileLayout ? (option.labelMobile ?? option.label) : ''}
            disabled={false}
            isMobileLayout={isMobileLayout}
            testID={`${testID}-${
              option.value
                ? option.value.toLowerCase().replace(/[^a-z0-9]/g, "-")
                : "unknown"
            }-${item.id}`}
          />
        ))}
      </View>
      {/* Action plan button beside the question (to the right of radio buttons) */}
      {isMobileLayout && shouldShowActionPlan && (
        <View style={styles.apMobileSeparator} />
      )}
      <View style={[styles.apColumnContainer, isMobileLayout && shouldShowActionPlan && styles.apColumnContainerMobile]}>
        {shouldShowActionPlan && (
          (hasExistingActionPlan && storedPlan) ? (
            <TouchableOpacity
              style={styles.actionPlanCompleted}
              onPress={() => onOpenActionPlan && onOpenActionPlan(item.id, item)}
              testID={`${testID}-edit-actionplan-${item.id}`}
            >
              <EditIcon />
              <Text style={[styles.actionPlanCompletedText, qIsDark && { color: qColors.brand.primary }]}>Action Plan</Text>
              <DLOTooltip
                content={`Supervisor: ${formatUserName(storedPlan.supervisor || storedPlan.assignee || 'N/A')}`}
                title={`Action Plan: ${storedPlan.actionPlan || ''}`}
                position="top"
                trigger="hover"
                maxWidth={260}
              >
                <InfoIcon />
              </DLOTooltip>
            </TouchableOpacity>
          ) : (
            <TouchableOpacity
              onPress={() => onOpenActionPlan && onOpenActionPlan(item.id, item)}
              testID={`${testID}-add-actionplan-${item.id}`}
            >
              <Text style={[styles.actionPlanLinkText, qIsDark && { color: qColors.brand.primary }]}>+ Add a Action Plan</Text>
            </TouchableOpacity>
          )
        )}
      </View>
    </View>
  );
};

// Column headers component exactly like TouchPointSurvey.tsx with added AP header
const ColumnHeaders: React.FC<{ testID?: string; subHeaderTitle?: string }> = ({ testID, subHeaderTitle }) => {
  // Headers are intentionally left blank — labels are shown per SubHeader now
  const headers: string[] = ["", "", ""];

  return (
    <View style={styles.columnHeadersContainer} testID={`${testID}-headers`}>
      <View style={styles.questionTextContainer}>
        <Text style={styles.columnHeaderText}></Text>
      </View>
      <View style={styles.radioButtonsContainer}>
        {headers.slice(0, 3).map((header, index) => (
          <View key={index} style={styles.columnHeaderItem}>
            {/* intentionally empty - labels rendered inside SubHeader */}
          </View>
        ))}
      </View>
      <View style={styles.apHeaderContainer}>
        {/* Intentionally empty: action plan control is shown per subheader below questions */}
      </View>
    </View>
  );
};

// Section header component exactly like TouchPointSurvey.tsx
const SectionHeader: React.FC<{ title: string; testID?: string }> = ({
  title,
  testID,
}) => (
  <View style={styles.sectionHeader} testID={testID}>
    <Text style={styles.sectionHeaderText}>{title}</Text>
  </View>
);

// Sub-header component styled as blue bar (title left, Yes/No/N/A labels right)
const SubHeader: React.FC<{
  title: string;
  isTop?: boolean;
  hasToggle?: boolean;
  toggleValue?: boolean;
  onTogglePress?: (newValue: boolean) => void;
  testID?: string;
  // controlled by parent to decide whether to show desktop/tablet layout
  isMobileLayout?: boolean;
  isTabletLayout?: boolean;
}> = ({ title, isTop = false, testID, isMobileLayout = false, isTabletLayout = false }) => {
  const labels = getCustomLabels(title);
  const isLighthouseExecution = !!(title && String(title).trim().toLowerCase() === 'lighthouse execution');
  // show labels in subheader when NOT mobile layout
  const showLabelsInSubHeader = !isMobileLayout;
  // When Lighthouse Execution on mobile or tablet, break longer labels into two lines so they fit visually.
  const formatLabelForSmall = (label?: string) => {
    if (!label) return label;
    if (isLighthouseExecution && (isMobileLayout || isTabletLayout)) {
      const parts = label.split(' ');
      if (parts.length >= 2) {
        return parts.slice(0, 1).join(' ') + "\n" + parts.slice(1).join(' ');
      }
    }
    return label;
  };
  const renderLabelElement = (label?: string) => {
    if (!label) return null;
    if (isLighthouseExecution && (isMobileLayout || isTabletLayout)) {
      const parts = label.split(' ');
      if (parts.length >= 2) {
        const first = parts.slice(0, 1).join(' ');
        const rest = parts.slice(1).join(' ');
        return (
          <View style={{ alignItems: 'center' }}>
            <Text style={[styles.subHeaderLabelText, styles.subHeaderLabelLine]}>{first}</Text>
            <Text style={[styles.subHeaderLabelText, styles.subHeaderLabelLine]}>{rest}</Text>
          </View>
        );
      }
    }
    return <Text style={styles.subHeaderLabelText}>{label}</Text>;
  };
  return (
    <View style={[styles.subHeaderBlue, isTop ? styles.subHeaderBlueTop : null, isLighthouseExecution && styles.subHeaderBlueLh]} testID={testID}>
      <View style={styles.subHeaderBlueContent}>
        <Text style={styles.subHeaderBlueText}>{title}</Text>
        {showLabelsInSubHeader ? (
          <View style={styles.subHeaderLabels}>
            {renderLabelElement(labels.Yes)}
            {renderLabelElement(labels.No)}
            {renderLabelElement(labels['N/A'])}
          </View>
        ) : null}
      </View>
    </View>
  );
};

const SiteEvalSurveyContainer: React.FC<SiteEvalSurveyContainerProps> = ({
  data,
  walkAnswers = {},
  closeOutAnswers = {},
  onRadioChange,
  onAnswersChange,
  onToggleChange,
  testID = "site-eval-survey",
  isViewOnlyMode = false,
  selectedEvalId,
}) => {
  const { colors, resolvedTheme } = useTheme();
  const isDark = resolvedTheme === "dark";
  const { width } = useWindowDimensions();
  const isMobileLayout = width < 768;
  const isTabletLayout = width >= 768 && width <= 1024;
  
  // Get action plans from Redux
  const actionPlans = useSelector(selectSiteEvalActionPlans);

  // If walkAnswers is provided, use it as the source of truth; otherwise, use local state (for legacy tabs)
  const [surveyAnswers, setSurveyAnswers] = useState<SurveyAnswers>({});
  // Process data into survey format - memoized to prevent infinite re-renders
  const surveyItems = React.useMemo(() => {
    const items = data.map((item) => ({
      id: item.id.toString(),
      text: item.SiteEvaluationaForm || item.ReviewFindings || "",
      type: (item.isHeader
        ? "header"
        : item.isParent || item.toggleOnly
        ? "subheader"
        : "question") as "header" | "subheader" | "question",
      isParent: item.isParent || item.toggleOnly,
      parentId: item.parentId?.toString(),
      visible: item.visible !== false,
      hasToggle: item.isParent || item.toggleOnly,
    }));
    console.log("Processed survey items:", items);
    return items;
  }, [data]);

  // Replace the useState for toggleStates with this:
  // Create a stable serialized version tracking only "yes" answers (which affect toggles)
  // to prevent unnecessary recalculations when other answers change
  const walkAnswersKey = React.useMemo(() => {
    if (Array.isArray(walkAnswers)) {
      const yesAnswers = walkAnswers
        .filter(a => a?.id && a.answer === "yes")
        .map(a => a.id)
        .sort(); // Sort for stable comparison
      return JSON.stringify(yesAnswers);
    }
    return '';
  }, [walkAnswers]);

  const closeOutAnswersKey = React.useMemo(() => {
    // Only track partners that exist
    const partners = Object.entries(closeOutAnswers || {})
      .filter(([_, val]) => val && typeof val === "object" && val.partner)
      .map(([id, _]) => id)
      .sort();
    return JSON.stringify(partners);
  }, [closeOutAnswers]);

  const initialToggleStates = React.useMemo(() => {
    const states: Record<string, boolean> = {};
    if (Array.isArray(walkAnswers)) {
      walkAnswers.forEach((ansObj: { id: string; answer?: string }) => {
        if (ansObj?.id && ansObj.answer === "yes") {
          states[ansObj.id] = true;
        }
      });
    } else if (closeOutAnswers && Object.keys(closeOutAnswers).length > 0) {
      Object.entries(closeOutAnswers).forEach(([id, val]) => {
        if (val && typeof val === "object" && val.partner) {
          states[id] = true;
        }
      });
    }
    return states;
  }, [walkAnswersKey, closeOutAnswersKey]); // Use optimized serialized keys

  const [toggleStates, setToggleStates] =
    useState<Record<string, boolean>>(initialToggleStates);

  // Use a ref to access current toggle states without causing callback recreations
  const toggleStatesRef = React.useRef(toggleStates);
  React.useEffect(() => {
    toggleStatesRef.current = toggleStates;
  }, [toggleStates]);

  // Use a ref to track the last synced states to prevent infinite loops
  const lastSyncedStates = React.useRef<string>('');

  // CRITICAL FIX: Sync toggleStates with initialToggleStates when API data loads
  // This ensures toggles are ON when reopening saved records with action plans
  // Only update if the states actually changed to prevent infinite loops
  React.useEffect(() => {
    const currentStatesKey = JSON.stringify(initialToggleStates);
    
    // Only update if this is different from what we last synced
    if (currentStatesKey !== lastSyncedStates.current) {
      console.log('[SiteEvalSurveyContainer] Syncing toggleStates with initialToggleStates:', initialToggleStates);
      setToggleStates(initialToggleStates);
      lastSyncedStates.current = currentStatesKey;
    }
  }, [initialToggleStates]);

  // Track which questions have action plans (both from walkAnswers and localStorage) - memoized
  // Only recalculate when actual action plans change, not all answers
  const actionPlanQuestionsKey = React.useMemo(() => {
    if (Array.isArray(walkAnswers)) {
      const withActionPlans = walkAnswers
        .filter(a => a?.id && a.actionPlan)
        .map(a => a.id)
        .sort();
      return JSON.stringify(withActionPlans);
    }
    return '';
  }, [walkAnswers]);

  const actionPlanQuestions = React.useMemo(() => {
    const plans: Record<string, boolean> = {};
    if (Array.isArray(walkAnswers)) {
      walkAnswers.forEach((ansObj: { id: string; actionPlan?: string }) => {
        if (ansObj && ansObj.id && ansObj.actionPlan) {
          plans[ansObj.id] = true;
        }
      });
    }
    
    // Also check Redux for stored action plans
    const surveyItemIds = surveyItems.map(item => item.id);
    surveyItemIds.forEach(itemId => {
      if (checkIfActionPlanExistsInRedux(actionPlans, itemId, selectedEvalId)) {
        plans[itemId] = true;
      }
    });
    
    return plans;
  }, [actionPlanQuestionsKey, surveyItems, selectedEvalId, actionPlans]);
  
  // Track which subheaders have action plans - memoized
  const subHeaderActionPlans = React.useMemo(() => {
    const plans: Record<string, boolean> = {};
    surveyItems.forEach(item => {
      if (item.type === 'subheader') {
        const hasActionPlan = checkIfActionPlanExistsInRedux(actionPlans, item.id, selectedEvalId);
        if (hasActionPlan) {
          plans[item.id] = true;
        }
      }
    });
    return plans;
  }, [surveyItems, selectedEvalId, actionPlans]);

  // Then modify handleToggleChange to be more precise:
  const handleToggleChange = useCallback(
    (id: string, value: boolean, item: SurveyItem) => {
      // Use ref to access current toggle states without causing dependency issues
      const currentToggleStates = toggleStatesRef.current;
      
      // Special case: If toggle is already ON and user clicks it (trying to set it to ON again)
      // This happens when reopening saved records - user wants to EDIT the action plan
      if (currentToggleStates[id] === true && value === true) {
        // Toggle is already ON, user is clicking to open/edit the action plan
        if (onToggleChange) onToggleChange(id, true, item.text);
        return;
      }

      // Only proceed if the value is actually changing (for OFF->ON or ON->OFF transitions)
      if (currentToggleStates[id] === value) return;

      // When turning OFF
      if (!value) {
        setToggleStates((prev) => ({ ...prev, [id]: false }));
        if (onToggleChange) onToggleChange(id, false, item.text);

        if (item.isParent) {
          const childItems = surveyItems.filter(
            (child) => child.parentId === id
          );
          setSurveyAnswers((prev) => {
            const newAnswers = { ...prev };
            childItems.forEach((child) => delete newAnswers[child.id]);
            return newAnswers;
          });
        }
      } else {
        // When turning ON (from OFF), notify parent to open overlay
        if (onToggleChange) onToggleChange(id, true, item.text);
      }
    },
    [surveyItems, onToggleChange] // Removed toggleStates from dependencies
  );
 
  const handleSelectAnswer = useCallback(
    (id: string, answer: SurveyAnswer) => {
      if (onRadioChange) {
        // Map "Yes"|"No"|"N/A" to 'yes'|'no'|'na'
        let mapped: "yes" | "no" | "na" = "yes";
        if (answer === "No") mapped = "no";
        else if (answer === "N/A") mapped = "na";
        onRadioChange(id, mapped);
      } else {
        setSurveyAnswers((prevAnswers) => {
          const newAnswers = { ...prevAnswers, [id]: answer };
          // Check completion - include all visible questions regardless of toggle state
          const questionItems = surveyItems.filter(
            (item) => item.type === "question" && item.visible !== false
          );
          const isComplete = questionItems.every(
            (item) =>
              newAnswers[item.id] &&
              ["Yes", "No", "N/A"].includes(newAnswers[item.id])
          );
          if (onAnswersChange) onAnswersChange(newAnswers, isComplete);
          return newAnswers;
        });
      }
    },
    [surveyItems, onRadioChange, onAnswersChange]
  );

  // Group items by headers and sub-headers
  const processedSections = React.useMemo(() => {
    const headerGroups: Record<
      string,
      {
        header: SurveyItem;
        subHeaders: Record<
          string,
          {
            subHeader: SurveyItem;
            questions: SurveyItem[];
          }
        >
        questions: SurveyItem[];
      }
    > = {};

    // First pass: identify headers
    surveyItems.forEach((item) => {
      if (item.type === "header") {
        headerGroups[item.id] = {
          header: item,
          subHeaders: {},
          questions: [],
        };
      }
    });

    // Track current header/subheader while assigning items
    let currentHeaderId: string = "";
    let currentSubHeaderId: string = "";

    // Second pass: identify sub-headers and assign them to the most recent header
    surveyItems.forEach((item) => {
      if (item.type === "header") {
        currentHeaderId = item.id;
      } else if (
        item.type === "subheader" &&
        currentHeaderId &&
        headerGroups[currentHeaderId]
      ) {
        headerGroups[currentHeaderId].subHeaders[item.id] = {
          subHeader: item,
          questions: [],
        };
      }
    });

    // Third pass: assign questions to their subheaders or headers
    currentSubHeaderId = "";
    currentHeaderId = "";

    surveyItems.forEach((item) => {
      if (item.type === "header") {
        currentHeaderId = item.id;
        currentSubHeaderId = "";
      } else if (item.type === "subheader") {
        currentSubHeaderId = item.id;
      } else if (item.type === "question") {
        // Try to assign to the current subheader first
        if (currentSubHeaderId && currentHeaderId) {
          const headerGroup = headerGroups[currentHeaderId];
          if (headerGroup && headerGroup.subHeaders[currentSubHeaderId]) {
            headerGroup.subHeaders[currentSubHeaderId].questions.push(item);
          }
        } else if (currentHeaderId && headerGroups[currentHeaderId]) {
          // If no current subheader, assign directly to header
          headerGroups[currentHeaderId].questions.push(item);
        }
      }
    });

    console.log("Processed sections:", Object.values(headerGroups));
    return Object.values(headerGroups);
  }, [surveyItems]);

  
  // Render a section with its headers and questions
  const renderSection = useCallback(
    (sectionData: any) => {
      return (
        <View
          key={sectionData.header.id}
          testID={`${testID}-section-${sectionData.header.id}`}
        >
          {/* Column headers removed — labels are rendered inside each SubHeader (blue bar) */}

          {/* Render sub-headers with their questions */}
          {Object.values(sectionData.subHeaders).map((subSection: any, idx: number) => {
            return (
              <View key={subSection.subHeader.id}>
                <SubHeader
                  title={subSection.subHeader.text}
                  isTop={idx === 0}
                  hasToggle={subSection.subHeader.hasToggle}
                  toggleValue={toggleStates[subSection.subHeader.id]}
                  onTogglePress={(newValue: boolean) =>
                    handleToggleChange(
                      subSection.subHeader.id,
                      newValue,
                      subSection.subHeader
                    )
                  }
                  testID={`${testID}-subheader-${subSection.subHeader.id}`}
                  isMobileLayout={isMobileLayout}
                  isTabletLayout={isTabletLayout}
                />

                {/* Show questions regardless of toggle state */}
                {subSection.questions.map((item: SurveyItem) => (
                  <QuestionRow
                    key={item.id}
                    item={item}
                    answer={surveyAnswers[item.id] || null}
                    onSelectAnswer={handleSelectAnswer}
                    isSubData={true}
                    hasActionPlan={actionPlanQuestions[item.id] || false}
                    storedPlan={getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId)}
                    testID={`${testID}-question`}
                    subHeaderTitle={subSection.subHeader.text}
                    onOpenActionPlan={(id, it) => handleToggleChange(id, true, it)}
                    isMobileLayout={isMobileLayout}
                    isViewOnlyMode={isViewOnlyMode}
                  />
                ))}

                {/* Single action plan control for this subheader */}
                {subSection.questions && subSection.questions.length > 0 && (
                  <View style={[styles.questionRow, isMobileLayout && styles.questionRowMobile]} testID={`${testID}-ap-row-${subSection.subHeader.id}`}>
                    {/* Place the AP control on the LEFT side (question text column) */}
                    <View style={[
                      styles.questionTextContainer, 
                      styles.apTextContainer,
                      isMobileLayout ? styles.questionTextContainerMobile : styles.questionTextContainerDesktop
                    ]}>
                      {/* Check if this subheader or any questions under it have action plans */}
                      {(subHeaderActionPlans[subSection.subHeader.id] || subSection.questions.some((q: SurveyItem) => actionPlanQuestions[q.id])) ? (
                        /* Show Figma design with edit and info icons */
                        <TouchableOpacity
                          style={styles.actionPlanCompleted}
                          onPress={() =>
                            handleToggleChange(
                              subSection.subHeader.id,
                              true,
                              subSection.subHeader
                            )
                          }
                          testID={`${testID}-edit-actionplan-sub-${subSection.subHeader.id}`}
                        >
                          <EditIcon />
                          <Text style={styles.actionPlanCompletedText}>Action Plan</Text>
                          <InfoIcon />
                        </TouchableOpacity>
                      ) : (
                        // Hide the Add link when the entire form is view-only/completed
                        !isViewOnlyMode ? (
                          <TouchableOpacity
                            onPress={() =>
                              handleToggleChange(
                                subSection.subHeader.id,
                                true,
                                subSection.subHeader
                              )
                            }
                            testID={`${testID}-add-actionplan-sub-${subSection.subHeader.id}`}
                          >
                            <Text style={styles.actionPlanLinkText}>+ Add a Action Plan</Text>
                          </TouchableOpacity>
                        ) : null
                      )}
                    </View>
                    <View style={[styles.radioButtonsContainer, isMobileLayout ? styles.radioButtonsContainerMobile : styles.radioButtonsContainerDesktop]} />
                    <View style={styles.apHeaderContainer} />
                  </View>
                )}

              </View>
            );
          })}

          {/* Render direct questions under header */}
          {sectionData.questions.map((item: SurveyItem) => (
            <QuestionRow
              key={item.id}
              item={item}
              answer={surveyAnswers[item.id] || null}
              onSelectAnswer={handleSelectAnswer}
              hasActionPlan={actionPlanQuestions[item.id] || false}
              storedPlan={getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId)}
              testID={`${testID}-question`}
              onOpenActionPlan={(id, it) => handleToggleChange(id, true, it)}
              isMobileLayout={isMobileLayout}
              isViewOnlyMode={isViewOnlyMode}
            />
          ))}
        </View>
      );
    },
    [
      surveyAnswers,
      toggleStates,
      handleSelectAnswer,
      testID,
      actionPlanQuestions,
      subHeaderActionPlans,
      isViewOnlyMode,
      isMobileLayout,
      isTabletLayout,
      actionPlans,
      selectedEvalId,
    ]
  );

  // Use walkAnswers (array) or closeOutAnswers (object) if provided, otherwise local state - memoized
  const answersToUse = React.useMemo(() => {
    let answers: SurveyAnswers = surveyAnswers;

    if (Array.isArray(walkAnswers) && walkAnswers.length > 0) {
      answers = {};
      walkAnswers.forEach(
        (ansObj: { id: string; answer?: string; actionPlan?: string }) => {
          if (ansObj && ansObj.id) {
            if (ansObj.answer) {
              if (ansObj.answer === "yes") {
                answers[ansObj.id] = "Yes";
              } else if (ansObj.answer === "no") {
                answers[ansObj.id] = "No";
              } else if (ansObj.answer === "na") {
                answers[ansObj.id] = "N/A";
              } else {
                answers[ansObj.id] = null;
              }
            }
          }
        }
      );
    } else if (closeOutAnswers && Object.keys(closeOutAnswers).length > 0) {
      answers = Object.fromEntries(
        Object.entries(closeOutAnswers).map(([id, val]) => {
          // For closeOutAnswers, just mark as answered if partner exists
          if (val && typeof val === "object" && val.partner) {
            return [id, "Yes"];
          }
          return [id, null];
        })
      );
    }
    return answers;
  }, [walkAnswers, closeOutAnswers, surveyAnswers]);

  // Removed redundant useEffect - toggleStates are already synced via initialToggleStates
  
  // Patch renderSection to use answersToUse
  const patchedRenderSection = useCallback(
    (sectionData: any) => {
      // Add console log for answer counts per h
      return (
        <View
          key={sectionData.header.id}
          testID={`${testID}-section-${sectionData.header.id}`}
        >
          {/* Column headers removed — labels are rendered inside each SubHeader (blue bar) */}
          {/* Render sub-headers with their questions */}
          {Object.values(sectionData.subHeaders).map((subSection: any, idx: number) => {
            return (
              <View key={subSection.subHeader.id}>
                <SubHeader
                  title={subSection.subHeader.text}
                  isTop={idx === 0}
                  hasToggle={subSection.subHeader.hasToggle}
                  toggleValue={toggleStates[subSection.subHeader.id]}
                  onTogglePress={(newValue: boolean) =>
                    handleToggleChange(
                      subSection.subHeader.id,
                      newValue,
                      subSection.subHeader
                    )
                  }
                  testID={`${testID}-subheader-${subSection.subHeader.id}`}
                  isMobileLayout={isMobileLayout}
                  isTabletLayout={isTabletLayout}
                />
                {/* Show questions regardless of toggle state */}
                {subSection.questions.map((item: SurveyItem) => (
                  <QuestionRow
                    key={item.id}
                    item={item}
                    answer={answersToUse[item.id] || null}
                    onSelectAnswer={handleSelectAnswer}
                    isSubData={true}
                    hasActionPlan={actionPlanQuestions[item.id] || false}
                    storedPlan={getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId)}
                    testID={`${testID}-question`}
                    subHeaderTitle={subSection.subHeader.text}
                    onOpenActionPlan={(id, it) => handleToggleChange(id, true, it)}
                    isMobileLayout={isMobileLayout}
                    isViewOnlyMode={isViewOnlyMode}
                  />
                ))}

              </View>
            );
          })}
          {/* Render direct questions under header */}
          {sectionData.questions.map((item: SurveyItem) => (
            <QuestionRow
              key={item.id}
              item={item}
              answer={answersToUse[item.id] || null}
              onSelectAnswer={handleSelectAnswer}
              hasActionPlan={actionPlanQuestions[item.id] || false}
              storedPlan={getStoredActionPlanFromRedux(actionPlans, item.id, selectedEvalId)}
              testID={`${testID}-question`}
              onOpenActionPlan={(id, it) => handleToggleChange(id, true, it)}
              isMobileLayout={isMobileLayout}
              isViewOnlyMode={isViewOnlyMode}
            />
          ))}
        </View>
      );
    },
    [
      answersToUse,
      toggleStates,
      handleSelectAnswer,
      testID,
      actionPlanQuestions,
      subHeaderActionPlans,
      isViewOnlyMode,
      isMobileLayout,
      isTabletLayout,
      actionPlans,
      selectedEvalId,
    ]
  );

  return (
    <View style={[styles.container, isDark && { backgroundColor: colors.background.surface }]} testID={testID}>
      <ScrollView style={styles.scrollView}>
        <View style={styles.contentContainer}>
          {processedSections.map(patchedRenderSection)}
        </View>
      </ScrollView>
    </View>
  );
};

// Styles exactly like TouchPointSurvey.tsx
const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: "#fff",
   
  },
  scrollView: {
    flex: 1,
  },
  sectionHeader: {
    backgroundColor: "#0066cc",
    padding: 12,
    borderRadius: 4,
    marginVertical: 8,
  },
  sectionHeaderText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
  },
  subHeaderBlue: {
    backgroundColor: Platform.OS === 'web' ? "#0b66bf" : "#034895",
    paddingVertical: Platform.OS === 'web' ? 10 : 12,
    paddingHorizontal: 0,
    marginBottom: 8,
    borderRadius: 2,
    position: 'relative', 
  },
  subHeaderBlueLh: {
    paddingVertical: Platform.OS === 'web' ? 18 : 20,
    minHeight: 56,
  },
  subHeaderBlueContent: {
    flexDirection: "row",
    alignItems: "center",
  },
  subHeaderBlueText: {
    color: "#fff",
    fontSize: 14,
    fontWeight: "600",
    flex: 1,
    paddingLeft: 20,
  },
  subHeaderLabels: {
    position: 'absolute',
    // move labels left so they sit above the radio buttons column
    right: 148,
    top: 0,
    bottom: 0,
    flexDirection: 'row',
    width: 260,
    justifyContent: 'space-between',
    alignItems: 'center',
    paddingRight: Platform.OS==="web"?10:0,
    paddingLeft: 0,
  },
  subHeaderLabelText: {
    color: '#fff',
    fontSize: Platform.OS === 'web' ? 13 : 12,
    fontWeight: '600',
    textAlign: 'center',
    width: Platform.OS=="web"?76:64,
    marginRight: 4,
    // Allow labels to wrap when we insert an explicit newline (\n) for iPad/tablet
    flexWrap: 'wrap',
    lineHeight: Platform.OS === 'web' ? 16 : 18,
    includeFontPadding: false,
  },
  subHeaderLabelLine: {
    lineHeight: Platform.OS === 'web' ? 16 : 18,
    paddingVertical: 0,
  },
  columnHeadersContainer: {
    flexDirection: "row",
    borderBottomWidth: 0,
    paddingVertical: 0,
  },
  columnHeaderText: {
    fontWeight: "600",
    color: "#444",
    fontSize: 12,
    textAlign: "center",
    lineHeight: 16,
  },
  columnHeaderItem: {
    flex: 1,
    alignItems: "center",
    paddingHorizontal: 2,
  },
  questionRow: {
    // Default: row layout (web)
    flexDirection: 'row',
    paddingVertical: 12,
    borderBottomWidth: 1,
    borderBottomColor: "#e9e9e9",
    alignItems: 'center',
    position: 'relative', // allow absolute positioning of radio column on desktop
  },
  // Mobile override: stack question above radio row
  questionRowMobile: {
    flexDirection: 'column',
    alignItems: 'flex-start',
  },
  // Left/content column takes remaining space
  questionTextContainer: {
    flex: 1,
    // base padding for small screens
    paddingRight: 24,
    paddingLeft: 8,
  },
  // Desktop/tablet variant reserves space on right for the absolute radio column
  questionTextContainerDesktop: {
    paddingRight: 24, 
    paddingLeft: 20,
  },
  questionTextContainerMobile: {
    // Make sure the question text spans full width and aligns slightly left above radio buttons
    paddingLeft: 8,
    paddingRight: 12,
  },
  // Radio row: full width on mobile and left-aligned under the question text.
  radioButtonsContainer: {
    // baseline styles; desktop/tablet variant will be inline instead of absolutely positioned
    width: Platform.OS === 'web' ? 240 : '100%',
    flexDirection: 'row',
    justifyContent: Platform.OS === 'web' ? 'space-between' : 'flex-start',
    paddingRight: 0,
    paddingLeft: Platform.OS === 'web' ? 0 : 24,
    marginTop: Platform.OS === 'web' ? 0 : 8,
    gap: Platform.OS === 'web' ? undefined : 16,
    alignItems: 'center',
  },
  radioButtonsContainerMobile: {
    // On mobile: left-align radio options and reduce spacing so they cluster on the left
    width: '100%',
    flexDirection: 'row',
    justifyContent: 'flex-start',
    // reduced horizontal padding so options start closer to the left edge
    paddingLeft: 12,
    paddingRight: 8,
    marginTop: 6,
    alignItems: 'center',
  },
  // Desktop/tablet radio column: keep inline so AP control sits to the right on same row
  radioButtonsContainerDesktop: {
    // switch from absolute to inline
    position: 'relative',
    // remove absolute offsets
    right: undefined,
    top: undefined,
    height: undefined,
    width: 240,
    flexDirection: 'row',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingRight: 0,
  },
  radioButtonContainer: {
    alignItems: "center",
    justifyContent: "center",
  },
  // Desktop fixed cell
  radioButtonContainerDesktop: {
    width: 70,
    marginRight: 0,
  },
  // Mobile flexible cell
  radioButtonContainerMobile: {
    flex: 0,
    width: 'auto',
    marginRight: 8,
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButtonInner: {
    flexDirection: Platform.OS === 'web' ? 'column' : 'row',
    alignItems: 'center',
    justifyContent: 'center',
  },
  radioButton: {
    // Desktop/web: keep existing circular 18x18. Mobile: use 12x20 (oval) as requested.
    height: Platform.OS === 'web' ? 18 : 14,
    width: Platform.OS === 'web' ? 18 : 14,
    borderRadius: Platform.OS === 'web' ? 9 : 9,
    borderWidth: 2,
    borderColor: "#0066cc",
    alignItems: "center",
    justifyContent: "center",
  },
  radioButtonSelected: {
    // inner selected dot: slightly smaller on mobile to match oval shape
    height: Platform.OS === 'web' ? 8 : 6,
    width: Platform.OS === 'web' ? 8 : 6,
    borderRadius: Platform.OS === 'web' ? 4 : 4,
    backgroundColor: "#0066cc",
  },
  radioButtonLabel: {
    fontSize: Platform.OS === 'web' ? 14 : 16,
    color: "#555",
    // On mobile when inner is row, add left margin; on web when column, add marginTop
    marginLeft: Platform.OS === 'web' ? 0 : 4,
    marginTop: Platform.OS === 'web' ? 6 : 0,
    // allow label to wrap (used on mobile to show two-line labels)
    textAlign: 'center',
    flexWrap: 'wrap',
    lineHeight: 18,
  },
  radioButtonContainerDisabled: {
    opacity: 0.5,
  },
  radioButtonDisabled: {
    borderColor: "#ccc",
  },
  radioButtonSelectedDisabled: {
    backgroundColor: "#ccc",
  },
  radioButtonLabelDisabled: {
    color: "#999",
  },
  apHeaderContainer: {
    width: 160,
    // keep the AP control aligned on the same baseline as the radios
    alignItems: 'flex-start',
    justifyContent: 'center',
    paddingLeft: 20,
  },
  apColumnContainer: {
    minWidth: 160,
    paddingLeft: 16,
    paddingRight: 8,
    alignItems: 'flex-start',
    justifyContent: 'center',
  },
  apColumnContainerMobile: {
    width: '100%',
    minWidth: undefined,
    paddingLeft: 12,
    paddingRight: 12,
    paddingVertical: 8,
    alignItems: 'flex-start',
  },
  apMobileSeparator: {
    width: '100%',
    height: 1,
    backgroundColor: '#E8EDF2',
    marginTop: 8,
  },
  actionPlanLink: {
    // keep the link inline with icon and text, no top margin so it sits on same line
    marginTop: 0,
    flexDirection: 'row',
    alignItems: 'center',
  },
  actionPlanLinkText: {
    color: '#0066cc',
    fontSize: Platform.OS === 'web' ? 12 : 14,
    fontWeight: '600',
  },
  actionPlanCompleted: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 4,
    paddingVertical: 4,
  },
  actionPlanCompletedText: {
    color: '#005CBC',
    fontSize: 12,
    fontWeight: '700',
    lineHeight: 18,
  },
  // Align AP control left edge with subheader title start (same left padding as subHeaderBlue)
  apTextContainer: {
    paddingLeft: 24,
  },
  // Content container which provides consistent horizontal padding matching Figma
  contentContainer: {
    paddingHorizontal: 0,
  },
  subHeaderBlueTop: {
    borderTopLeftRadius: 8,
    borderTopRightRadius: 8,
  },
  questionText: {
    fontSize: 15,
    color: "#333",
    paddingBottom: Platform.OS === 'web' ? 0 : 6,
    flexWrap: 'wrap',
    // allow text to shrink when container is constrained
    flexShrink: 1,
  },
  subDataRow: {
    marginLeft: 0,
  },
  subDataText: {
    fontFamily: FontFamily.regular,
    fontSize: 12,
    fontStyle: 'normal',
    fontWeight: '500',
    color: '#2D2D2D',
    lineHeight: 18,
    paddingLeft: 8,
  },
});

export default SiteEvalSurveyContainer;


